import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

const EMPTY_TASK_COMPOSER_FORM = { name: '', owner_id: '', project_id: '' };
const EMPTY_OBJECTIVE_COMPOSER_FORM = { name: '', project_id: '', owner_id: '', effort: '1', description: '' };

// Fermeture envoyée par le serveur pour une réunion clôturée : pas de reconnexion.
export const MEETING_CLOSED_CODE = 4001;
const RECONNECT_MAX_DELAY_MS = 15000;
// Après une saisie locale sur une tension, les échos du serveur ne l'écrasent pas pendant ce délai.
const LOCAL_TENSION_EDIT_HOLD_MS = 1500;
// Brouillon de nouvelle tension : envoyé après une courte pause de frappe ; ce que renvoie le serveur pendant la
// saisie (resynchronisation) n'écrase pas le texte en cours.
const DRAFT_SEND_DEBOUNCE_MS = 150;
const LOCAL_DRAFT_HOLD_MS = 3000;
const PREPARED_POINTS_REFRESH_MS = 30000;
// Relevé des tensions de gouvernance : filet de sécurité ; le temps réel pousse déjà chaque changement.
const TENSIONS_POLL_MS = 5000;
const TENSIONS_POLL_LIVE_MS = 20000;

// Bornes de la modification qui mène de `base` à `text` : début, fin (dans base) et texte inséré.
const spliceOf = (base, text) => {
  let start = 0;
  const max = Math.min(base.length, text.length);
  while (start < max && base[start] === text[start]) start += 1;
  let endBase = base.length;
  let endText = text.length;
  while (endBase > start && endText > start && base[endBase - 1] === text[endText - 1]) {
    endBase -= 1;
    endText -= 1;
  }
  return { start, end: endBase, insert: text.slice(start, endText) };
};

/**
 * Fusion à trois du bloc-notes partagé (même règle que le serveur, services/meeting_notes_merge.py) : la saisie locale
 * (`local`, partie de `base`) est rejouée sur `remote`. Deux modifications à des endroits différents sont gardées
 * toutes les deux ; si elles se chevauchent, `local` l'emporte.
 */
export const mergeText = (base, local, remote) => {
  const b = base || '';
  const l = local || '';
  const r = remote || '';
  if (l === b) return r;
  if (r === b || r === l) return l;
  const mine = spliceOf(b, l);
  const theirs = spliceOf(b, r);
  if (mine.end <= theirs.start) {
    return r.slice(0, mine.start) + mine.insert + r.slice(mine.end);
  }
  if (theirs.end <= mine.start) {
    const shift = theirs.insert.length - (theirs.end - theirs.start);
    return r.slice(0, mine.start + shift) + mine.insert + r.slice(mine.end + shift);
  }
  return l;
};

const normalizeObjectiveEffort = (value, fallback = '1') => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isInteger(parsed) && parsed >= 0) return String(parsed);
  const fallbackParsed = Number.parseInt(fallback, 10);
  if (Number.isInteger(fallbackParsed) && fallbackParsed >= 0) return String(fallbackParsed);
  return '1';
};

const asIdString = (value) => (value !== undefined && value !== null && value !== '' ? String(value) : '');

const buildEmptyPointComposer = (projectId = null) => ({
  mode: null,
  point_id: null,
  task_form: {
    ...EMPTY_TASK_COMPOSER_FORM,
    project_id: projectId ? String(projectId) : '',
  },
  objective_form: {
    ...EMPTY_OBJECTIVE_COMPOSER_FORM,
    project_id: projectId ? String(projectId) : '',
  },
});

const buildMeetingSignature = (meetings) => {
  if (!Array.isArray(meetings)) return '';
  return meetings
    .map((meeting) =>
      [
        meeting?.id,
        meeting?.circle_id,
        meeting?.status,
        meeting?.created_by,
        meeting?.updated_at,
        meeting?.started_at,
        meeting?.meeting_phase,
        meeting?.connected,
        meeting?.pending_tensions,
        meeting?.open_points,
      ]
        .map((value) => String(value ?? ''))
        .join(':')
    )
    .sort()
    .join('|');
};

export const isPastTensionStatus = (status) => {
  const normalized = (status || '').toLowerCase();
  return normalized === 'applied' || normalized.startsWith('reject');
};

const readErrorDetail = async (response, fallback) => {
  try {
    const data = await response.json();
    if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  } catch (err) {
    // réponse sans JSON
  }
  return fallback;
};

const normalizeProjectReviewContext = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  const rawProjectId = entry.project_id ?? entry.current?.project_id ?? entry.last?.project_id;
  if (rawProjectId === undefined || rawProjectId === null || rawProjectId === '') return null;
  return {
    project_id: Number(rawProjectId),
    current: entry.current || null,
    last: entry.last || null,
  };
};

const mergeProjectReviewContext = (items, review) => {
  if (!review || review.project_id === undefined || review.project_id === null) return items;
  const key = String(review.project_id);
  const nextItems = Array.isArray(items) ? [...items] : [];
  const index = nextItems.findIndex((item) => String(item?.project_id) === key);
  if (index >= 0) {
    nextItems[index] = { ...nextItems[index], current: review };
    return nextItems;
  }
  nextItems.push({ project_id: Number(review.project_id), current: review, last: null });
  return nextItems;
};

export const EMPTY_LIVE = {
  active: false,
  mac_connected: false,
  project_notes: [],
  point_notes: [],
  project_order: [],
  phase: null,
  project: null,
  lastError: null,
};

const upsertBy = (items, item, key) => {
  const list = Array.isArray(items) ? items : [];
  const index = list.findIndex((entry) => String(entry?.[key]) === String(item?.[key]));
  if (index < 0) return [...list, item];
  const next = [...list];
  next[index] = item;
  return next;
};

// Messages « live_* » diffusés par la secrétaire en direct.
// « live_error » est local : dernière erreur renvoyée par le serveur, pour débloquer un formulaire en attente.
export const reduceLive = (prev, data) => {
  switch (data.type) {
    case 'live_status':
      if (data.active === false) return { ...prev, active: false, mac_connected: Boolean(data.mac_connected), warning: null };
      return { ...prev, ...data, type: undefined, warning: data.warning || null };
    case 'live_context':
      return { ...prev, phase: data.phase, project: data.project, project_order: data.project_order || prev.project_order };
    case 'live_project_notes':
      return { ...prev, project_notes: upsertBy(prev.project_notes, data.note, 'project_id') };
    case 'live_point_notes':
      return { ...prev, point_notes: upsertBy(prev.point_notes, data.note, 'point_id') };
    case 'live_error':
      return { ...prev, lastError: { message: data.message || 'Action impossible', at: data.at || Date.now() } };
    default:
      return prev;
  }
};

const baseSnapshot = () => ({ name: '', purpose: '', domains: [], accountabilities: [], parent_id: null });

const normalizeMeetingDraftValue = (draft) => {
  const baseDraft = {
    id: null,
    circle_id: null,
    title: '',
    summary: '',
    status: 'draft',
    expressed_by_role_id: '',
    action: 'edit',
    original_data: baseSnapshot(),
    proposed_data: baseSnapshot(),
    operations: [],
  };
  if (!draft) return baseDraft;
  let operations = draft.operations || [];
  if (!Array.isArray(operations) && typeof operations === 'string') {
    try {
      operations = JSON.parse(operations);
    } catch (err) {
      operations = [];
    }
  }
  if (!Array.isArray(operations)) operations = [];
  return {
    ...baseDraft,
    ...draft,
    operations,
    original_data: draft.original_data || baseSnapshot(),
    proposed_data: draft.proposed_data || baseSnapshot(),
  };
};

const normalizePointComposerValue = (composer, fallbackProjectId = null) => {
  const base = buildEmptyPointComposer(fallbackProjectId);
  if (!composer || typeof composer !== 'object') return base;
  const mode = composer.mode === 'task' || composer.mode === 'objective' ? composer.mode : null;
  const pointId = asIdString(composer.point_id) || null;
  const taskInput = composer.task_form && typeof composer.task_form === 'object' ? composer.task_form : {};
  const objectiveInput = composer.objective_form && typeof composer.objective_form === 'object' ? composer.objective_form : {};
  return {
    mode,
    point_id: pointId,
    task_form: {
      name: taskInput.name || '',
      owner_id: asIdString(taskInput.owner_id),
      project_id: asIdString(taskInput.project_id),
    },
    objective_form: {
      name: objectiveInput.name || '',
      project_id: asIdString(objectiveInput.project_id) || (fallbackProjectId ? String(fallbackProjectId) : ''),
      owner_id: asIdString(objectiveInput.owner_id),
      effort: normalizeObjectiveEffort(objectiveInput.effort, base.objective_form.effort),
      description: objectiveInput.description || '',
    },
  };
};

/**
 * État partagé des réunions de cercle (gouvernance, tactique, pilotage) et connexion temps réel.
 *
 * Rappels optionnels :
 * - onStructureChanged({ reason, meetingId }) : la structure des cercles a pu changer. `reason` vaut
 *   'decision_applied' (une tension de la réunion vient d'être appliquée) ou 'meeting_closed'.
 * - onTensionsChanged({ meetingId }) : les tensions de la réunion ont changé (import, création, décision) ;
 *   utile pour rafraîchir la liste « Importer une tension ».
 */
const useMeetingState = ({
  apiUrl,
  getAuthHeaders,
  user,
  selectedCircle,
  setSelectedCircle,
  activeTab,
  setActiveTab,
  circles,
  normalizeTension,
  onStructureChanged,
  onTensionsChanged,
}) => {
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [meetingTensions, setMeetingTensions] = useState([]);
  const [meetingParticipants, setMeetingParticipants] = useState([]);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [meetingPresenceCount, setMeetingPresenceCount] = useState(0);
  const [meetingConnectedUsers, setMeetingConnectedUsers] = useState([]);
  const [meetingRealtimeConnected, setMeetingRealtimeConnected] = useState(false);
  const [meetingFormVisible, setMeetingFormVisible] = useState(false);
  const [meetingPhase, setMeetingPhase] = useState('inclusion');
  const [selectedMeetingTensionIdShared, setSelectedMeetingTensionIdShared] = useState(null);
  const [meetingLoading, setMeetingLoading] = useState(false);
  const [meetingError, setMeetingError] = useState(null);
  const [meetingPoints, setMeetingPoints] = useState([]);
  const [meetingPreparedPoints, setMeetingPreparedPoints] = useState([]);
  const [meetingPreparedPointsLoading, setMeetingPreparedPointsLoading] = useState(false);
  const [meetingPreparedPointsError, setMeetingPreparedPointsError] = useState(null);
  const [selectedMeetingPointId, setSelectedMeetingPointId] = useState(null);
  const [selectedMeetingPointIdShared, setSelectedMeetingPointIdShared] = useState(null);
  const [selectedMeetingProjectId, setSelectedMeetingProjectId] = useState(null);
  const [selectedMeetingProjectIdShared, setSelectedMeetingProjectIdShared] = useState(null);
  const [meetingProjectReviews, setMeetingProjectReviews] = useState([]);
  const [meetingPointComposer, setMeetingPointComposer] = useState(buildEmptyPointComposer());
  const [openMeetings, setOpenMeetings] = useState([]);
  const [meetingHistory, setMeetingHistory] = useState([]);
  const [meetingHistoryLoading, setMeetingHistoryLoading] = useState(false);
  const [meetingHistoryError, setMeetingHistoryError] = useState(null);
  const [showMeetingHistory, setShowMeetingHistory] = useState(false);
  const [meetingDraft, setMeetingDraft] = useState(() => normalizeMeetingDraftValue(null));
  const [selectedMeetingTensionId, setSelectedMeetingTensionId] = useState(null);
  const [meetingLive, setMeetingLive] = useState(EMPTY_LIVE);
  // Focus tech : présentation en cours (sans les gestes) et Focus proposés à la réunion.
  const [meetingFocus, setMeetingFocus] = useState(null);
  const [meetingFocusList, setMeetingFocusList] = useState([]);
  // Gestes et état du présentateur sur la diapo en cours : hors de l'état React (plusieurs dizaines par seconde),
  // gardés pour rattraper (arrivée en cours, retour au direct) et passés aux abonnés (la diapo affichée).
  const focusLogRef = useRef({ slide: 0, events: [], state: null });
  const focusListenersRef = useRef(new Set());

  const effectiveUserId = user?.employee_id ?? user?.id;
  const activeMeetingId = activeMeeting?.id ?? null;
  const activeMeetingType = String(activeMeeting?.type || 'gouvernance').toLowerCase();
  const activeMeetingSecretaryId = activeMeeting?.created_by ?? null;
  const userIsSecretary = Boolean(activeMeeting) && String(activeMeetingSecretaryId) === String(effectiveUserId);

  const meetingSocketRef = useRef(null);
  const meetingNoteDebounce = useRef(null);
  // Bloc-notes : texte du serveur dont part la saisie locale, saisie locale, envoi en attente de son écho.
  // Ce navigateur se reconnaît dans les échos par `clientId` (deux onglets de la même personne restent distincts).
  const clientIdRef = useRef(`c${Math.random().toString(36).slice(2, 10)}`);
  const notesBaseRef = useRef('');
  const notesLocalRef = useRef('');
  const notesInFlightRef = useRef(null);
  const notesInFlightAt = useRef(0);
  const draftSendTimer = useRef(null);
  const lastDraftEditAt = useRef(0);
  const lastCreatedDraft = useRef(null);
  const realtimeConnectedRef = useRef(false);
  const tensionEditDebounce = useRef(null);
  const pendingTensionUpdate = useRef(null);
  const localTensionEdit = useRef({ id: null, until: 0 });
  const pendingPointToTension = useRef(null);
  const meetingRejoinPending = useRef(false);
  const meetingPointNoteDebounce = useRef(null);
  const openMeetingRequestSeq = useRef(0);
  const preparedPointsRequestSeq = useRef(0);
  const preparedPointsMeetingRef = useRef(null);
  const tensionsObserved = useRef({ meetingId: null, signature: null, applied: new Set() });
  const closedNotified = useRef(new Set());
  const meetingDraftRef = useRef(meetingDraft);
  const selectedCircleIdRef = useRef(selectedCircle?.id ?? null);
  selectedCircleIdRef.current = selectedCircle?.id ?? null;
  const selectedTensionIdRef = useRef(selectedMeetingTensionId);
  selectedTensionIdRef.current = selectedMeetingTensionId;
  const selectedSharedTensionIdRef = useRef(selectedMeetingTensionIdShared);
  selectedSharedTensionIdRef.current = selectedMeetingTensionIdShared;
  const userIsSecretaryRef = useRef(userIsSecretary);
  userIsSecretaryRef.current = userIsSecretary;
  const activeMeetingRef = useRef(activeMeeting);
  activeMeetingRef.current = activeMeeting;
  const effectiveUserIdRef = useRef(effectiveUserId);
  effectiveUserIdRef.current = effectiveUserId;
  const getAuthHeadersRef = useRef(getAuthHeaders);
  getAuthHeadersRef.current = getAuthHeaders;
  const normalizeTensionRef = useRef(normalizeTension);
  normalizeTensionRef.current = normalizeTension;
  const structureChangedRef = useRef(onStructureChanged);
  structureChangedRef.current = onStructureChanged;
  const tensionsChangedRef = useRef(onTensionsChanged);
  tensionsChangedRef.current = onTensionsChanged;
  const setActiveTabRef = useRef(setActiveTab);
  setActiveTabRef.current = setActiveTab;
  const setSelectedCircleRef = useRef(setSelectedCircle);
  setSelectedCircleRef.current = setSelectedCircle;

  const callSafely = useCallback((ref, payload) => {
    const callback = ref.current;
    if (typeof callback !== 'function') return;
    try {
      callback(payload);
    } catch (err) {
      console.error('Rappel de réunion en échec', err);
    }
  }, []);

  // Tensions reçues (sync, poll, décision) : prévient la page des tensions appliquées et des changements.
  const observeMeetingTensions = useCallback((meetingId, tensions) => {
    const list = Array.isArray(tensions) ? tensions : [];
    const signature = list.map((t) => `${t.id}:${t.status}`).sort().join('|');
    const applied = new Set(list.filter((t) => String(t.status || '').toLowerCase() === 'applied').map((t) => String(t.id)));
    const previous = tensionsObserved.current;
    const sameMeeting = previous.meetingId !== null && String(previous.meetingId) === String(meetingId);
    tensionsObserved.current = { meetingId, signature, applied };
    if (!sameMeeting) return;
    if ([...applied].some((id) => !previous.applied.has(id))) {
      callSafely(structureChangedRef, { reason: 'decision_applied', meetingId });
    }
    if (previous.signature !== signature) {
      callSafely(tensionsChangedRef, { meetingId });
    }
  }, [callSafely]);

  // Une saisie locale récente sur une tension n'est pas écrasée par un écho ou un rafraîchissement.
  const keepLocalTensionEdit = useCallback((incoming) => (prev) => {
    const { id, until } = localTensionEdit.current;
    const holding = id !== null && (Date.now() < until || pendingTensionUpdate.current);
    if (!holding) return incoming;
    const local = (prev || []).find((t) => String(t.id) === String(id));
    if (!local) return incoming;
    return incoming.map((t) => (String(t.id) === String(id)
      ? {
        ...t,
        title: local.title,
        summary: local.summary,
        action: local.action,
        expressed_by_role_id: local.expressed_by_role_id,
        proposed_data: local.proposed_data,
        operations: local.operations,
      }
      : t));
  }, []);

  const resetMeetingScopedState = useCallback(() => {
    if (meetingNoteDebounce.current) clearTimeout(meetingNoteDebounce.current);
    if (tensionEditDebounce.current) clearTimeout(tensionEditDebounce.current);
    if (meetingPointNoteDebounce.current) clearTimeout(meetingPointNoteDebounce.current);
    if (draftSendTimer.current) clearTimeout(draftSendTimer.current);
    meetingNoteDebounce.current = null;
    tensionEditDebounce.current = null;
    meetingPointNoteDebounce.current = null;
    draftSendTimer.current = null;
    lastDraftEditAt.current = 0;
    notesBaseRef.current = '';
    notesLocalRef.current = '';
    notesInFlightRef.current = null;
    pendingTensionUpdate.current = null;
    localTensionEdit.current = { id: null, until: 0 };
    pendingPointToTension.current = null;
    const emptyDraft = normalizeMeetingDraftValue(null);
    meetingDraftRef.current = emptyDraft;
    setMeetingDraft(emptyDraft);
    setMeetingTensions([]);
    setMeetingParticipants([]);
    setMeetingNotes('');
    setMeetingPresenceCount(0);
    setMeetingConnectedUsers([]);
    setMeetingFormVisible(false);
    setMeetingPhase('inclusion');
    setSelectedMeetingTensionIdShared(null);
    setSelectedMeetingTensionId(null);
    setMeetingPoints([]);
    setMeetingPreparedPoints([]);
    setMeetingPreparedPointsLoading(false);
    setMeetingPreparedPointsError(null);
    setSelectedMeetingPointId(null);
    setSelectedMeetingPointIdShared(null);
    setSelectedMeetingProjectId(null);
    setSelectedMeetingProjectIdShared(null);
    setMeetingProjectReviews([]);
    setMeetingPointComposer(buildEmptyPointComposer());
    setMeetingLive(EMPTY_LIVE);
    setMeetingFocus(null);
    setMeetingFocusList([]);
    focusLogRef.current = { slide: 0, events: [], state: null };
  }, []);

  // Réunion clôturée (par nous ou par un autre) : état vidé, structure à recharger, une seule fois.
  const handleMeetingClosed = useCallback((meetingId) => {
    resetMeetingScopedState();
    setActiveMeeting((prev) => (prev && (meetingId === undefined || String(prev.id) === String(meetingId)) ? null : prev));
    setOpenMeetings((prev) => (prev || []).filter((m) => String(m?.id) !== String(meetingId)));
    const key = String(meetingId);
    if (!closedNotified.current.has(key)) {
      closedNotified.current.add(key);
      callSafely(structureChangedRef, { reason: 'meeting_closed', meetingId });
    }
  }, [resetMeetingScopedState, callSafely]);

  const fetchMeetingTensions = useCallback(async (meetingId) => {
    if (!meetingId) return;
    try {
      const response = await fetch(`${apiUrl}/circle-meetings/${meetingId}/tensions`, {
        headers: getAuthHeadersRef.current(),
      });
      if (!response.ok) throw new Error(await readErrorDetail(response, 'Impossible de récupérer les tensions de la réunion'));
      const data = await response.json();
      const normalized = (data || []).map((t) => normalizeTensionRef.current(t));
      setMeetingTensions(keepLocalTensionEdit(normalized));
      observeMeetingTensions(meetingId, normalized);
      // La tension affichée est celle du serveur (choisie par le secrétaire), la même pour tous : le relevé ne la
      // change pas. Sans choix partagé (réunion qui démarre), le secrétaire montre la première à traiter à tous.
      const exists = (id) => id !== null && id !== undefined && normalized.some((t) => String(t.id) === String(id));
      if (!exists(selectedSharedTensionIdRef.current) && !exists(selectedTensionIdRef.current)) {
        const first = normalized.find((t) => !isPastTensionStatus(t.status));
        setSelectedMeetingTensionId(first ? first.id : null);
        const socket = meetingSocketRef.current;
        if (first && userIsSecretaryRef.current && socket && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'select_tension', tension_id: first.id }));
        }
      }
    } catch (err) {
      setMeetingError(err.message);
    }
  }, [apiUrl, keepLocalTensionEdit, observeMeetingTensions]);

  const fetchMeetingParticipants = useCallback(async (meetingId) => {
    if (!meetingId) return;
    try {
      const response = await fetch(`${apiUrl}/circle-meetings/${meetingId}/participants`, {
        headers: getAuthHeadersRef.current(),
      });
      if (!response.ok) throw new Error(await readErrorDetail(response, 'Impossible de récupérer les participants'));
      const data = await response.json();
      setMeetingParticipants(data || []);
    } catch (err) {
      setMeetingError(err.message);
    }
  }, [apiUrl]);

  const fetchMeetingPreparedPoints = useCallback(async (meetingIdOverride) => {
    const meetingId = meetingIdOverride || activeMeetingId;
    if (!meetingId) {
      setMeetingPreparedPoints([]);
      return;
    }
    const requestSeq = ++preparedPointsRequestSeq.current;
    // Le chargement ne s'affiche qu'au premier passage : les rafraîchissements restent discrets.
    if (String(preparedPointsMeetingRef.current) !== String(meetingId)) {
      setMeetingPreparedPointsLoading(true);
    }
    try {
      setMeetingPreparedPointsError(null);
      const response = await fetch(`${apiUrl}/circle-meetings/${meetingId}/prep-points`, {
        headers: getAuthHeadersRef.current(),
      });
      if (!response.ok) throw new Error(await readErrorDetail(response, 'Impossible de récupérer les points préparés'));
      const data = await response.json();
      if (requestSeq !== preparedPointsRequestSeq.current) return;
      preparedPointsMeetingRef.current = meetingId;
      setMeetingPreparedPoints(Array.isArray(data) ? data : []);
    } catch (err) {
      if (requestSeq !== preparedPointsRequestSeq.current) return;
      setMeetingPreparedPointsError(err.message);
    } finally {
      if (requestSeq === preparedPointsRequestSeq.current) setMeetingPreparedPointsLoading(false);
    }
  }, [activeMeetingId, apiUrl]);

  const fetchOpenMeeting = useCallback(async (circleId) => {
    if (!circleId) return;
    const requestSeq = ++openMeetingRequestSeq.current;
    try {
      setMeetingLoading(true);
      const response = await fetch(`${apiUrl}/circle-meetings/open?circle_id=${circleId}`, {
        headers: getAuthHeadersRef.current(),
      });
      if (!response.ok) throw new Error(await readErrorDetail(response, 'Impossible de récupérer les réunions'));
      const data = await response.json();
      if (requestSeq !== openMeetingRequestSeq.current) return;
      if (String(selectedCircleIdRef.current ?? '') !== String(circleId)) return;
      const meeting = data && data.length > 0 ? data[0] : null;
      // Même réunion : on garde l'objet (et son secrétaire temps réel) pour ne rien relancer. Déjà dans une réunion :
      // on y reste en parcourant la carte (regarder un rôle pendant la gouvernance ne fait plus quitter la séance) ;
      // « Rejoindre » sur un autre cercle change de réunion.
      setActiveMeeting((prev) => {
        if (prev && meeting && String(prev.id) === String(meeting.id)) return prev;
        if (prev) return prev;
        return meeting;
      });
      setMeetingError(null);
    } catch (err) {
      if (requestSeq !== openMeetingRequestSeq.current) return;
      if (String(selectedCircleIdRef.current ?? '') !== String(circleId)) return;
      setMeetingError(err.message);
    } finally {
      if (requestSeq === openMeetingRequestSeq.current) {
        setMeetingLoading(false);
      }
    }
  }, [apiUrl]);

  const fetchOpenMeetingsForUser = useCallback(async () => {
    if (!effectiveUserId) {
      setOpenMeetings([]);
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/circle-meetings/open-for-user`, {
        headers: getAuthHeadersRef.current(),
      });
      if (!response.ok) throw new Error('Impossible de récupérer les réunions ouvertes');
      const data = await response.json();
      const next = Array.isArray(data) ? data : [];
      setOpenMeetings((prev) => {
        const prevSig = buildMeetingSignature(prev);
        const nextSig = buildMeetingSignature(next);
        return prevSig === nextSig ? prev : next;
      });
    } catch (err) {
      setOpenMeetings([]);
    }
  }, [apiUrl, effectiveUserId]);

  useEffect(() => {
    fetchOpenMeetingsForUser();
    const interval = setInterval(fetchOpenMeetingsForUser, 10000);
    const handleFocus = () => fetchOpenMeetingsForUser();
    window.addEventListener('focus', handleFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchOpenMeetingsForUser]);

  const openMeetingsByCircle = useMemo(() => {
    const map = new Map();
    (openMeetings || []).forEach((meeting) => {
      if (meeting?.circle_id !== undefined && meeting?.circle_id !== null) {
        const key = String(meeting.circle_id);
        if (!map.has(key)) map.set(key, meeting);
      }
    });
    return map;
  }, [openMeetings]);

  const openMeetingCircleIds = useMemo(() => new Set(openMeetingsByCircle.keys()), [openMeetingsByCircle]);

  useEffect(() => {
    if (selectedCircle?.id) {
      fetchOpenMeeting(selectedCircle.id);
    } else {
      openMeetingRequestSeq.current += 1;
      setMeetingLoading(false);
    }
  }, [selectedCircle?.id, fetchOpenMeeting]);

  // Chargement à l'ouverture d'une réunion ; le relevé périodique des tensions ne sert qu'en gouvernance (filet de
  // sécurité : espacé quand le temps réel est connecté).
  useEffect(() => {
    if (!activeMeetingId) return undefined;
    fetchMeetingParticipants(activeMeetingId);
    if (activeMeetingType !== 'gouvernance') return undefined;
    fetchMeetingTensions(activeMeetingId);
    let timer = null;
    const tick = () => {
      timer = setTimeout(() => {
        fetchMeetingTensions(activeMeetingId);
        tick();
      }, realtimeConnectedRef.current ? TENSIONS_POLL_LIVE_MS : TENSIONS_POLL_MS);
    };
    tick();
    return () => clearTimeout(timer);
  }, [activeMeetingId, activeMeetingType, fetchMeetingTensions, fetchMeetingParticipants]);

  // Points préparés (réunion tactique) : rechargés quand l'ordre du jour change (import, point clos),
  // quand le secrétaire change, et de temps en temps pour voir les nouveaux points des absents.
  const meetingPointsSignature = useMemo(
    () => (meetingPoints || []).map((p) => `${p.id}:${p.status}:${p.circle_point_id ?? ''}`).join('|'),
    [meetingPoints]
  );
  useEffect(() => {
    if (!activeMeetingId || activeMeetingType !== 'tactique') {
      preparedPointsRequestSeq.current += 1;
      preparedPointsMeetingRef.current = null;
      setMeetingPreparedPoints([]);
      setMeetingPreparedPointsLoading(false);
      return undefined;
    }
    fetchMeetingPreparedPoints(activeMeetingId);
    const id = setInterval(() => fetchMeetingPreparedPoints(activeMeetingId), PREPARED_POINTS_REFRESH_MS);
    return () => clearInterval(id);
  }, [activeMeetingId, activeMeetingType, activeMeetingSecretaryId, meetingPointsSignature, fetchMeetingPreparedPoints]);

  useEffect(() => {
    if (activeTab === 'meeting' && !activeMeeting && !meetingLoading && !meetingRejoinPending.current) {
      setActiveTabRef.current('structure');
    }
  }, [activeTab, activeMeeting, meetingLoading]);

  const sendMeetingRealtime = useCallback((payload) => {
    const ws = meetingSocketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  // Bloc-notes : un seul envoi à la fois, avec le texte du serveur dont il part ; le serveur fusionne s'il a changé
  // entre-temps et renvoie le résultat à tous. Hors ligne, la saisie reste locale et repart à la reconnexion.
  const flushNotes = useCallback(() => {
    if (meetingNoteDebounce.current) {
      clearTimeout(meetingNoteDebounce.current);
      meetingNoteDebounce.current = null;
    }
    if (notesInFlightRef.current !== null) {
      // Repartira à l'écho ; sans écho depuis longtemps, on redemande l'état au serveur (qui fait foi).
      if (Date.now() - notesInFlightAt.current > 8000) sendMeetingRealtime({ type: 'sync_request' });
      return;
    }
    const content = notesLocalRef.current;
    if (content === notesBaseRef.current) return;
    if (sendMeetingRealtime({ type: 'note_update', content, base: notesBaseRef.current, client: clientIdRef.current })) {
      notesInFlightRef.current = content;
      notesInFlightAt.current = Date.now();
    }
  }, [sendMeetingRealtime]);

  // Brouillon de nouvelle tension reçu (écho, autre secrétaire, resynchronisation). Le secrétaire ne s'écrase pas
  // avec l'écho de sa propre saisie, ni avec une resynchronisation pendant qu'il tape.
  const applyIncomingDraft = useCallback((rawDraft, by) => {
    const mine = by !== null && by !== undefined && String(by) === String(effectiveUserIdRef.current);
    if (mine) return;
    if (userIsSecretaryRef.current) {
      const typing = Boolean(draftSendTimer.current) || Date.now() - lastDraftEditAt.current < LOCAL_DRAFT_HOLD_MS;
      // Resynchronisation (reconnexion) : seul le secrétaire écrit le brouillon, le sien est le plus récent.
      const resync = (by === null || by === undefined) && lastDraftEditAt.current > 0;
      if (typing || resync) return;
    }
    const draft = normalizeMeetingDraftValue(rawDraft);
    meetingDraftRef.current = draft;
    setMeetingDraft(draft);
  }, []);

  const scheduleNotes = useCallback((delay = 300) => {
    if (meetingNoteDebounce.current) clearTimeout(meetingNoteDebounce.current);
    meetingNoteDebounce.current = setTimeout(() => {
      meetingNoteDebounce.current = null;
      flushNotes();
    }, delay);
  }, [flushNotes]);

  // Texte du serveur reçu : la saisie locale pas encore partie est rejouée dessus.
  const applyServerNotes = useCallback((content, from) => {
    const server = content || '';
    const local = mergeText(from, notesLocalRef.current, server);
    notesBaseRef.current = server;
    notesLocalRef.current = local;
    setMeetingNotes(local);
    if (local !== server) scheduleNotes();
  }, [scheduleNotes]);

  // Connexion temps réel : une seule socket active ; reconnexion automatique (délai croissant).
  useEffect(() => {
    const meetingId = activeMeetingId;
    resetMeetingScopedState();
    if (!meetingId) {
      setMeetingRealtimeConnected(false);
      if (meetingSocketRef.current) {
        const previous = meetingSocketRef.current;
        meetingSocketRef.current = null;
        try {
          previous.close();
        } catch (err) {
          // déjà fermée
        }
      }
      return undefined;
    }

    let disposed = false;
    let attempt = 0;
    let retryTimer = null;
    let closedByServer = false;

    const handleMessage = (data) => {
      if (!data || typeof data.type !== 'string') return;
      if (data.type === 'sync_state') {
        setMeetingError(null);
        // Un envoi resté sans écho (coupure) : s'il a changé le texte du serveur, on le considère passé.
        const sent = notesInFlightRef.current;
        notesInFlightRef.current = null;
        const server = data.notes?.content || '';
        applyServerNotes(server, sent !== null && server !== notesBaseRef.current ? sent : notesBaseRef.current);
        if (Array.isArray(data.tensions)) {
          const normalized = data.tensions.map((t) => normalizeTensionRef.current(t));
          setMeetingTensions(keepLocalTensionEdit(normalized));
          observeMeetingTensions(meetingId, normalized);
        }
        if (data.participants) setMeetingParticipants(data.participants);
        if (data.meeting) {
          setActiveMeeting((prev) => (prev && String(prev.id) === String(data.meeting.id)
            ? { ...prev, ...data.meeting }
            : prev));
        }
        if (data.draft !== undefined) applyIncomingDraft(data.draft, null);
        if (typeof data.connected === 'number') setMeetingPresenceCount(data.connected);
        if (Array.isArray(data.connected_users)) setMeetingConnectedUsers(data.connected_users);
        if (typeof data.form_visible === 'boolean') setMeetingFormVisible(data.form_visible);
        if (Array.isArray(data.points)) setMeetingPoints(data.points);
        if (data.meeting_phase !== undefined) {
          setMeetingPhase(data.meeting_phase || 'inclusion');
        }
        if (data.selected_point_id !== undefined) {
          setSelectedMeetingPointIdShared(data.selected_point_id);
          if (data.selected_point_id !== null) {
            setSelectedMeetingPointId(data.selected_point_id);
          }
        }
        if (data.selected_project_id !== undefined) {
          setSelectedMeetingProjectIdShared(data.selected_project_id);
          setSelectedMeetingProjectId(data.selected_project_id || null);
        }
        setMeetingPointComposer(normalizePointComposerValue(
          data.point_composer !== undefined ? data.point_composer : null,
          data.selected_project_id || null
        ));
        if (Array.isArray(data.project_reviews)) {
          setMeetingProjectReviews((prev) =>
            data.project_reviews.reduce(
              (items, review) => mergeProjectReviewContext(items, review),
              prev
            )
          );
        }
        if (data.secretary_id !== undefined) {
          setActiveMeeting((prev) => (prev && String(prev.created_by) !== String(data.secretary_id)
            ? { ...prev, created_by: data.secretary_id }
            : prev));
        }
        if (data.selected_tension_id !== undefined) {
          setSelectedMeetingTensionIdShared(data.selected_tension_id);
          if (data.selected_tension_id !== null) {
            setSelectedMeetingTensionId(data.selected_tension_id);
          }
        }
        if (data.live) setMeetingLive({ ...EMPTY_LIVE, ...data.live });
        if (data.focus !== undefined) {
          const { log, state, ...focus } = data.focus || {};
          setMeetingFocus(data.focus ? focus : null);
          focusLogRef.current = { slide: focus.slide || 0, events: Array.isArray(log) ? log : [], state: state ?? null };
        }
        if (Array.isArray(data.focus_list)) setMeetingFocusList(data.focus_list);
      } else if (data.type === 'focus_update') {
        const focus = data.focus || null;
        setMeetingFocus((prev) => {
          if (!focus || !prev || String(prev.focus_id) !== String(focus.focus_id)) {
            focusLogRef.current = { slide: focus?.slide || 0, events: [], state: null };
          }
          return focus;
        });
        if (Array.isArray(data.focus_list)) setMeetingFocusList(data.focus_list);
      } else if (data.type === 'focus_slide') {
        focusLogRef.current = { slide: data.slide, events: [], state: null };
        setMeetingFocus((prev) => (prev ? { ...prev, slide: data.slide } : prev));
      } else if (data.type === 'focus_events' || data.type === 'focus_state') {
        const log = focusLogRef.current;
        if (data.slide !== log.slide) return;
        if (data.type === 'focus_events' && Array.isArray(data.events)) log.events.push(...data.events);
        if (data.type === 'focus_state') log.state = data.state;
        focusListenersRef.current.forEach((listener) => {
          try {
            listener(data);
          } catch (err) {
            // un abonné en erreur ne bloque pas les autres
          }
        });
      } else if (data.type.startsWith('live_')) {
        setMeetingLive((prev) => reduceLive(prev, data));
      } else if (data.type === 'note_update') {
        if (data.client && data.client === clientIdRef.current) {
          // Écho de notre envoi (fusionné par le serveur si quelqu'un a écrit entre-temps) : ce qu'on a tapé depuis
          // est rejoué dessus.
          const sent = notesInFlightRef.current ?? notesBaseRef.current;
          notesInFlightRef.current = null;
          applyServerNotes(data.content, sent);
          return;
        }
        // Un envoi est en route : le serveur fusionnera ce texte avec lui et nous renverra le résultat.
        if (notesInFlightRef.current !== null) return;
        applyServerNotes(data.content, notesBaseRef.current);
      } else if (data.type === 'tensions_sync') {
        lastCreatedDraft.current = null;
        const normalized = (data.tensions || []).map((t) => normalizeTensionRef.current(t));
        setMeetingTensions(keepLocalTensionEdit(normalized));
        observeMeetingTensions(meetingId, normalized);
        if (data.selected_tension_id !== undefined) {
          setSelectedMeetingTensionIdShared(data.selected_tension_id);
          if (data.selected_tension_id !== null) {
            setSelectedMeetingTensionId(data.selected_tension_id);
          }
        }
      } else if (data.type === 'points_sync') {
        setMeetingPoints(Array.isArray(data.points) ? data.points : []);
        if (data.selected_point_id !== undefined) {
          setSelectedMeetingPointIdShared(data.selected_point_id);
          if (data.selected_point_id !== null) {
            setSelectedMeetingPointId(data.selected_point_id);
          }
        }
      } else if (data.type === 'draft_update') {
        applyIncomingDraft(data.draft, data.by);
      } else if (data.type === 'point_selected') {
        setSelectedMeetingPointIdShared(data.point_id);
        setSelectedMeetingPointId(data.point_id || null);
      } else if (data.type === 'project_selected') {
        setSelectedMeetingProjectIdShared(data.project_id);
        setSelectedMeetingProjectId(data.project_id || null);
      } else if (data.type === 'point_composer') {
        setMeetingPointComposer((prev) => normalizePointComposerValue(data.composer, prev?.objective_form?.project_id || null));
      } else if (data.type === 'project_review_update') {
        if (data.review) {
          setMeetingProjectReviews((prev) => mergeProjectReviewContext(prev, data.review));
        }
      } else if (data.type === 'presence') {
        setMeetingPresenceCount(typeof data.connected === 'number' ? data.connected : 0);
        if (Array.isArray(data.users)) setMeetingConnectedUsers(data.users);
        if (data.secretary_id !== undefined) {
          setActiveMeeting((prev) => (prev && String(prev.created_by) !== String(data.secretary_id)
            ? { ...prev, created_by: data.secretary_id }
            : prev));
        }
      } else if (data.type === 'participants_update') {
        if (Array.isArray(data.participants)) setMeetingParticipants(data.participants);
      } else if (data.type === 'secretary_update') {
        setActiveMeeting((prev) => (prev && String(prev.created_by) !== String(data.secretary_id)
          ? { ...prev, created_by: data.secretary_id }
          : prev));
      } else if (data.type === 'tension_selected') {
        setSelectedMeetingTensionIdShared(data.tension_id);
        setSelectedMeetingTensionId(data.tension_id || null);
      } else if (data.type === 'form_visibility') {
        if (typeof data.visible === 'boolean') setMeetingFormVisible(data.visible);
      } else if (data.type === 'phase_update') {
        if (data.phase !== undefined) setMeetingPhase(data.phase || 'inclusion');
      } else if (data.type === 'meeting_closed') {
        closedByServer = true;
        handleMeetingClosed(data.meeting_id ?? meetingId);
      } else if (data.type === 'error') {
        const message = data.message || 'Erreur temps réel';
        setMeetingError(message);
        // Création de tension refusée juste après l'envoi : la saisie revient dans le formulaire.
        const created = lastCreatedDraft.current;
        lastCreatedDraft.current = null;
        if (created && Date.now() - created.at < 5000 && userIsSecretaryRef.current) {
          meetingDraftRef.current = created.draft;
          setMeetingDraft(created.draft);
          setMeetingFormVisible(true);
          const socket = meetingSocketRef.current;
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'form_toggle', visible: true }));
            socket.send(JSON.stringify({ type: 'draft_update', draft: created.draft }));
          }
        }
        setMeetingLive((prev) => reduceLive(prev, { type: 'live_error', message, at: Date.now() }));
      }
    };

    const connect = () => {
      if (disposed) return;
      const authHeaders = getAuthHeadersRef.current();
      const token = (authHeaders?.Authorization || '').replace(/Bearer\s+/i, '');
      if (!token) {
        setMeetingRealtimeConnected(false);
        return;
      }
      const wsBase = (apiUrl || '').replace(/^http/i, 'ws');
      const socket = new WebSocket(`${wsBase}/circle-meetings/${meetingId}/ws?token=${encodeURIComponent(token)}`);
      meetingSocketRef.current = socket;
      const isReconnect = attempt > 0;
      socket.onopen = () => {
        if (meetingSocketRef.current !== socket) return;
        attempt = 0;
        realtimeConnectedRef.current = true;
        setMeetingRealtimeConnected(true);
        // Le serveur envoie l'état complet à la connexion (sync_state) : pas de deuxième demande.
        if (isReconnect && userIsSecretaryRef.current) {
          // Saisies du secrétaire faites pendant la coupure : elles repartent (le relevé ne les a pas écrasées).
          if (pendingTensionUpdate.current) {
            socket.send(JSON.stringify(pendingTensionUpdate.current));
            pendingTensionUpdate.current = null;
          }
          if (lastDraftEditAt.current) {
            socket.send(JSON.stringify({ type: 'draft_update', draft: meetingDraftRef.current }));
          }
        }
      };
      socket.onclose = (event) => {
        // Une ancienne socket (réunion précédente, reconnexion) ne touche plus à l'état.
        if (meetingSocketRef.current !== socket) return;
        meetingSocketRef.current = null;
        realtimeConnectedRef.current = false;
        setMeetingRealtimeConnected(false);
        setMeetingPresenceCount(0);
        setMeetingConnectedUsers([]);
        if (disposed) return;
        if (closedByServer || event?.code === MEETING_CLOSED_CODE) {
          if (!closedByServer) handleMeetingClosed(meetingId);
          return;
        }
        if (event?.code === 1008) {
          setMeetingError(event.reason || 'Connexion à la réunion refusée');
          return;
        }
        const delay = Math.min(RECONNECT_MAX_DELAY_MS, 1000 * (2 ** attempt));
        attempt += 1;
        retryTimer = setTimeout(connect, delay);
      };
      socket.onerror = () => {
        if (meetingSocketRef.current === socket) setMeetingRealtimeConnected(false);
      };
      socket.onmessage = (event) => {
        if (meetingSocketRef.current !== socket) return;
        let data = null;
        try {
          data = JSON.parse(event.data);
        } catch (err) {
          console.error('WebSocket parse error', err);
          return;
        }
        handleMessage(data);
      };
    };

    connect();
    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      const socket = meetingSocketRef.current;
      meetingSocketRef.current = null;
      realtimeConnectedRef.current = false;
      setMeetingRealtimeConnected(false);
      if (socket) {
        try {
          socket.close();
        } catch (err) {
          // déjà fermée
        }
      }
    };
  }, [activeMeetingId, apiUrl, resetMeetingScopedState, keepLocalTensionEdit, observeMeetingTensions, handleMeetingClosed,
    applyServerNotes, applyIncomingDraft]);

  // Plus secrétaire : le brouillon des autres secrétaires s'affiche de nouveau tel quel.
  useEffect(() => {
    if (!userIsSecretary) lastDraftEditAt.current = 0;
  }, [userIsSecretary]);

  // Brouillon de nouvelle tension : mis à jour tout de suite à l'écran, envoyé après une courte pause de frappe
  // (une frappe = un message faisait revenir des échos en retard sur la saisie).
  const handleMeetingDraftChange = useCallback(
    (updater) => {
      const prev = meetingDraftRef.current;
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const normalized = normalizeMeetingDraftValue(next);
      meetingDraftRef.current = normalized;
      setMeetingDraft(normalized);
      if (!userIsSecretary) return;
      lastDraftEditAt.current = Date.now();
      if (draftSendTimer.current) clearTimeout(draftSendTimer.current);
      draftSendTimer.current = setTimeout(() => {
        draftSendTimer.current = null;
        sendMeetingRealtime({ type: 'draft_update', draft: meetingDraftRef.current });
      }, DRAFT_SEND_DEBOUNCE_MS);
    },
    [userIsSecretary, sendMeetingRealtime]
  );

  const handleTransferSecretary = useCallback(
    (nextSecretaryId) => {
      if (!activeMeeting || !nextSecretaryId) return;
      if (!userIsSecretary) return;
      const sent = sendMeetingRealtime({ type: 'secretary_transfer', user_id: nextSecretaryId });
      if (!sent) {
        setMeetingError('Transfert du secrétaire impossible (connexion temps réel).');
      }
    },
    [activeMeeting, userIsSecretary, sendMeetingRealtime]
  );

  const handleMeetingNotesChange = useCallback((content) => {
    notesLocalRef.current = content || '';
    setMeetingNotes(content || '');
    scheduleNotes();
  }, [scheduleNotes]);

  const fetchMeetingProjectReviews = useCallback(
    async (projectIds = []) => {
      if (!activeMeetingId) {
        setMeetingProjectReviews([]);
        return [];
      }
      const normalizedIds = Array.isArray(projectIds)
        ? projectIds.filter((id) => id !== null && id !== undefined && id !== '')
        : [];
      const query = normalizedIds.length > 0
        ? `?project_ids=${encodeURIComponent(normalizedIds.join(','))}`
        : '';
      try {
        const response = await fetch(`${apiUrl}/circle-meetings/${activeMeetingId}/project-reviews${query}`, {
          headers: getAuthHeadersRef.current(),
        });
        if (!response.ok) throw new Error(await readErrorDetail(response, 'Impossible de récupérer les revues de projets'));
        const data = await response.json();
        const normalized = (Array.isArray(data) ? data : [])
          .map(normalizeProjectReviewContext)
          .filter(Boolean);
        setMeetingProjectReviews(normalized);
        return normalized;
      } catch (err) {
        setMeetingError(err.message);
        return [];
      }
    },
    [activeMeetingId, apiUrl]
  );

  const handleUpdateMeetingProjectReview = useCallback(
    async (projectId, review) => {
      if (!activeMeetingId || !projectId) return null;
      if (!userIsSecretary) return null;
      const payload = {
        ...(review || {}),
        project_id: Number(projectId),
      };
      const sent = sendMeetingRealtime({
        type: 'project_review_update',
        project_id: Number(projectId),
        review: payload,
      });
      if (sent) return payload;
      try {
        const response = await fetch(`${apiUrl}/circle-meetings/${activeMeetingId}/project-reviews/${projectId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeadersRef.current() },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error(await readErrorDetail(response, 'Mise à jour de la revue du projet impossible'));
        const updated = await response.json();
        setMeetingProjectReviews((prev) => mergeProjectReviewContext(prev, updated));
        return updated;
      } catch (err) {
        setMeetingError(err.message);
        return null;
      }
    },
    [activeMeetingId, userIsSecretary, apiUrl, sendMeetingRealtime]
  );

  // Ouvre une réunion (gouvernance, tactique ou pilotage) ; si une réunion du même type est déjà ouverte
  // pour ce cercle, le serveur la renvoie et on la rejoint.
  const handleOpenMeeting = async (type = 'gouvernance', circleOverride = selectedCircle) => {
    if (!circleOverride?.id) return;
    try {
      setMeetingLoading(true);
      setMeetingError(null);
      const response = await fetch(`${apiUrl}/circle-meetings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeadersRef.current() },
        body: JSON.stringify({ circle_id: circleOverride.id, type }),
      });
      if (!response.ok) throw new Error(await readErrorDetail(response, 'Création de réunion impossible'));
      const meeting = await response.json();
      closedNotified.current.delete(String(meeting.id));
      setActiveMeeting((prev) => (prev && String(prev.id) === String(meeting.id) ? prev : meeting));
      setSelectedCircle(circleOverride);
      setActiveTab('meeting');
      fetchOpenMeetingsForUser();
    } catch (err) {
      setMeetingError(err.message);
    } finally {
      setMeetingLoading(false);
    }
  };

  const handleCloseMeeting = async () => {
    if (!activeMeeting) return;
    const confirmed = typeof window === 'undefined' || window.confirm(
      'Clôturer la réunion ? Elle passera dans l’historique du cercle et ne pourra plus être modifiée.'
    );
    if (!confirmed) return;
    const closedId = activeMeeting.id;
    try {
      setMeetingLoading(true);
      const response = await fetch(`${apiUrl}/circle-meetings/${closedId}/close`, {
        method: 'POST',
        headers: getAuthHeadersRef.current(),
      });
      if (!response.ok) throw new Error(await readErrorDetail(response, 'Clôture impossible'));
      setMeetingError(null);
      handleMeetingClosed(closedId);
      fetchOpenMeetingsForUser();
    } catch (err) {
      setMeetingError(err.message);
    } finally {
      setMeetingLoading(false);
    }
  };

  const handleOpenMeetingHistory = useCallback(
    async (circleOverride = selectedCircle) => {
      if (!circleOverride?.id) return;
      try {
        setMeetingHistoryLoading(true);
        setMeetingHistoryError(null);
        setShowMeetingHistory(true);
        const response = await fetch(`${apiUrl}/circle-meetings/history?circle_id=${circleOverride.id}`, {
          headers: getAuthHeadersRef.current(),
        });
        if (!response.ok) throw new Error(await readErrorDetail(response, "Impossible de récupérer l'historique"));
        const data = await response.json();
        setMeetingHistory(Array.isArray(data) ? data : []);
      } catch (err) {
        setMeetingHistoryError(err.message);
      } finally {
        setMeetingHistoryLoading(false);
      }
    },
    [apiUrl, selectedCircle]
  );

  const handleCloseMeetingHistory = useCallback(() => {
    setShowMeetingHistory(false);
  }, []);

  const handleImportMeetingTension = async (tensionId) => {
    if (!activeMeeting || !tensionId) return;
    if (sendMeetingRealtime({ type: 'tension_import', tension_id: tensionId })) return;
    try {
      const response = await fetch(`${apiUrl}/circle-meetings/${activeMeeting.id}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeadersRef.current() },
        body: JSON.stringify({ tension_id: tensionId }),
      });
      if (!response.ok) throw new Error(await readErrorDetail(response, 'Import impossible'));
      await fetchMeetingTensions(activeMeeting.id);
      callSafely(tensionsChangedRef, { meetingId: activeMeeting.id });
    } catch (err) {
      setMeetingError(err.message);
    }
  };

  const handleToggleMeetingForm = useCallback((visible) => {
    const next = typeof visible === 'boolean' ? visible : !meetingFormVisible;
    // Formulaire fermé sans créer de tension : le point reste ouvert.
    if (!next) pendingPointToTension.current = null;
    setMeetingFormVisible(next);
    sendMeetingRealtime({ type: 'form_toggle', visible: next });
  }, [meetingFormVisible, sendMeetingRealtime]);

  const handleCreateMeetingTension = async () => {
    if (!activeMeeting) return;
    const draft = normalizeMeetingDraftValue(meetingDraftRef.current);
    const ops = Array.isArray(draft.operations) ? draft.operations : [];
    if (ops.length === 0) {
      setMeetingError('Aucune opération définie');
      return;
    }
    const firstOp = ops[0] || {};
    const action = firstOp.action || draft.action || 'edit';
    const targetCircle = (circles || []).find((circle) =>
      String(circle.id) === String(firstOp.target_circle_id || firstOp.circle_id || draft.circle_id)
    );
    const title = draft.title?.trim()
      || firstOp.data?.name
      || targetCircle?.name
      || 'Tension en réunion';
    const pointId = pendingPointToTension.current;
    const payload = {
      type: 'tension_add',
      title,
      summary: draft.summary,
      action,
      circle_id: draft.circle_id,
      expressed_by_role_id: draft.expressed_by_role_id ? Number(draft.expressed_by_role_id) : null,
      operations: ops,
      // Point de l'ordre du jour transformé en tension : marqué par le serveur une fois la tension créée.
      point_id: pointId || null,
    };
    const sent = sendMeetingRealtime(payload);
    if (sent) {
      // Le formulaire se vide tout de suite ; si le serveur refuse, la saisie revient (voir le message « error »).
      lastCreatedDraft.current = { draft, at: Date.now() };
      pendingPointToTension.current = null;
      handleMeetingDraftChange(normalizeMeetingDraftValue(null));
      // Formulaire refermé : la nouvelle tension s'affiche au centre, pour tous.
      setMeetingFormVisible(false);
      sendMeetingRealtime({ type: 'form_toggle', visible: false });
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/circle-meetings/${activeMeeting.id}/tensions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeadersRef.current() },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(await readErrorDetail(response, 'Création impossible'));
      pendingPointToTension.current = null;
      handleMeetingDraftChange(normalizeMeetingDraftValue(null));
      await fetchMeetingTensions(activeMeeting.id);
    } catch (err) {
      setMeetingError(err.message);
    }
  };

  const handleJoinMeeting = useCallback(
    (circleOrMeeting) => {
      if (!circleOrMeeting) return;
      const meeting = circleOrMeeting.circle_id
        ? circleOrMeeting
        : openMeetingsByCircle.get(String(circleOrMeeting.id));
      const circle = circleOrMeeting.circle_id
        ? circles.find((c) => String(c.id) === String(circleOrMeeting.circle_id))
        : circleOrMeeting;
      if (!meeting || !circle) return;
      setSelectedCircleRef.current(circle);
      setActiveTabRef.current('meeting');
      setMeetingError(null);
      if (activeMeeting?.id && String(activeMeeting.id) === String(meeting.id)) {
        meetingRejoinPending.current = true;
        setActiveMeeting(null);
        setTimeout(() => {
          setActiveMeeting(meeting);
          meetingRejoinPending.current = false;
        }, 0);
      } else {
        meetingRejoinPending.current = false;
        setActiveMeeting(meeting);
      }
    },
    [openMeetingsByCircle, circles, activeMeeting]
  );

  const handleAddMeetingPoint = useCallback(
    (title) => {
      const trimmed = (title || '').trim();
      if (!trimmed) return false;
      if (!sendMeetingRealtime({ type: 'point_add', title: trimmed })) {
        setMeetingError('Ajout du point impossible (connexion temps réel).');
        return false;
      }
      return true;
    },
    [sendMeetingRealtime]
  );

  const handleImportMeetingPoint = useCallback(
    (pointId) => {
      if (!activeMeeting || !pointId) return false;
      if (!sendMeetingRealtime({ type: 'point_import', point_id: pointId })) {
        setMeetingError("Ajout du point à l'ordre du jour impossible (connexion temps réel).");
        return false;
      }
      return true;
    },
    [activeMeeting, sendMeetingRealtime]
  );

  const handleSelectMeetingPoint = useCallback(
    (pointId) => {
      if (!activeMeeting || !userIsSecretary) return;
      setSelectedMeetingPointId(pointId);
      setMeetingPointComposer(buildEmptyPointComposer(selectedMeetingProjectId));
      sendMeetingRealtime({ type: 'point_select', point_id: pointId });
    },
    [activeMeeting, userIsSecretary, sendMeetingRealtime, selectedMeetingProjectId]
  );

  const handleMeetingPointDecision = useCallback(
    (pointId, status) => {
      if (!activeMeeting || !userIsSecretary) return;
      if (!sendMeetingRealtime({ type: 'point_decision', point_id: pointId, status })) {
        setMeetingError('Mise à jour du point impossible (connexion temps réel).');
      }
    },
    [activeMeeting, userIsSecretary, sendMeetingRealtime]
  );

  // « Transformer en tension » : ouvre le formulaire prérempli ; le point n'est marqué « Transformé en tension »
  // qu'une fois la tension réellement créée (annuler le formulaire le laisse ouvert).
  const handleMeetingPointToTension = useCallback(
    (point) => {
      if (!activeMeeting || !point || !userIsSecretary) return;
      pendingPointToTension.current = point.id;
      const base = normalizeMeetingDraftValue(null);
      handleMeetingDraftChange({ ...base, title: point.title || '' });
      setMeetingFormVisible(true);
      sendMeetingRealtime({ type: 'form_toggle', visible: true });
    },
    [activeMeeting, userIsSecretary, handleMeetingDraftChange, sendMeetingRealtime]
  );

  const handleMeetingPointNotesChange = useCallback(
    (pointId, notes) => {
      if (!activeMeeting || !pointId) return;
      if (meetingPointNoteDebounce.current) clearTimeout(meetingPointNoteDebounce.current);
      meetingPointNoteDebounce.current = setTimeout(() => {
        meetingPointNoteDebounce.current = null;
        if (!sendMeetingRealtime({ type: 'point_note', point_id: pointId, notes: notes || '' })) {
          setMeetingError('Mise à jour des notes impossible (connexion temps réel).');
        }
      }, 300);
    },
    [activeMeeting, sendMeetingRealtime]
  );

  const handleMeetingPointComposerUpdate = useCallback(
    (nextComposer) => {
      if (!activeMeeting || !userIsSecretary) return false;
      const normalized = normalizePointComposerValue(nextComposer, selectedMeetingProjectId);
      setMeetingPointComposer(normalized);
      if (!sendMeetingRealtime({ type: 'point_composer_update', composer: normalized })) {
        setMeetingError('Synchronisation du formulaire impossible (connexion temps réel).');
        return false;
      }
      return true;
    },
    [activeMeeting, userIsSecretary, selectedMeetingProjectId, sendMeetingRealtime]
  );

  const handleSelectMeetingProject = useCallback(
    (projectId) => {
      if (!activeMeeting || !userIsSecretary) return;
      setSelectedMeetingProjectId(projectId);
      sendMeetingRealtime({ type: 'project_select', project_id: projectId });
    },
    [activeMeeting, userIsSecretary, sendMeetingRealtime]
  );

  const handleSelectMeetingPhase = useCallback(
    (phase) => {
      if (!activeMeeting || !userIsSecretary) return;
      const nextPhase = phase || 'inclusion';
      setMeetingPhase(nextPhase);
      sendMeetingRealtime({ type: 'phase_select', phase: nextPhase });
    },
    [activeMeeting, userIsSecretary, sendMeetingRealtime]
  );

  const handleMeetingPointAction = useCallback(
    (pointId, action) => {
      if (!activeMeeting || !pointId || !action || !userIsSecretary) return;
      if (!sendMeetingRealtime({ type: 'point_action', point_id: pointId, action })) {
        setMeetingError("Ajout de l'action impossible (connexion temps réel).");
      }
    },
    [activeMeeting, userIsSecretary, sendMeetingRealtime]
  );

  const handleInviteMeetingParticipant = useCallback(
    async (employeeId) => {
      if (!activeMeetingId || !employeeId) return;
      try {
        const response = await fetch(`${apiUrl}/circle-meetings/${activeMeetingId}/invites`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeadersRef.current() },
          body: JSON.stringify({ employee_id: employeeId }),
        });
        if (!response.ok) throw new Error(await readErrorDetail(response, 'Invitation impossible'));
        const data = await response.json();
        if (Array.isArray(data?.participants)) {
          setMeetingParticipants(data.participants);
        } else {
          fetchMeetingParticipants(activeMeetingId);
        }
      } catch (err) {
        setMeetingError(err.message);
      }
    },
    [activeMeetingId, apiUrl, fetchMeetingParticipants]
  );

  const handleCreateTacticalObjective = useCallback(
    async (pointId, payload) => {
      if (!payload?.project_id || !payload?.name) return null;
      try {
        const effortValue = Number(payload.effort);
        const normalizedEffort = Number.isFinite(effortValue) && effortValue >= 0
          ? Math.floor(effortValue)
          : 1;
        const response = await fetch(`${apiUrl}/projects/${payload.project_id}/objectives`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeadersRef.current() },
          body: JSON.stringify({
            project_id: payload.project_id,
            name: payload.name,
            owner_id: payload.owner_id || null,
            sprint_id: payload.sprint_id || null,
            effort: normalizedEffort,
            description: payload.description || null,
          }),
        });
        if (!response.ok) throw new Error(await readErrorDetail(response, "Création de l'objectif impossible"));
        const created = await response.json();
        handleMeetingPointAction(pointId, {
          type: 'objective',
          objective_id: created.id,
          name: created.name,
          project_id: created.project_id,
          owner_id: created.owner_id || null,
        });
        return created;
      } catch (err) {
        setMeetingError(err.message);
        return null;
      }
    },
    [apiUrl, handleMeetingPointAction]
  );

  const handleCreateTacticalTask = useCallback(
    async (pointId, payload) => {
      if (!payload?.name || !payload?.owner_id) return null;
      try {
        const response = await fetch(`${apiUrl}/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeadersRef.current() },
          body: JSON.stringify({
            name: payload.name,
            owner_id: payload.owner_id,
            project_id: payload.project_id || null,
          }),
        });
        if (!response.ok) throw new Error(await readErrorDetail(response, 'Création de la tâche impossible'));
        const created = await response.json();
        handleMeetingPointAction(pointId, {
          type: 'task',
          task_id: created.id,
          name: created.name,
          owner_id: created.owner_id || null,
          project_id: created.project_id || null,
        });
        return created;
      } catch (err) {
        setMeetingError(err.message);
        return null;
      }
    },
    [apiUrl, handleMeetingPointAction]
  );

  const handleUpdateProjectNews = useCallback(
    (projectId, news) => {
      if (!activeMeeting || !projectId || !userIsSecretary) return false;
      const sent = sendMeetingRealtime({ type: 'project_news_update', project_id: Number(projectId), news });
      if (!sent) setMeetingError('Enregistrement impossible (connexion temps réel).');
      return sent;
    },
    [activeMeeting, userIsSecretary, sendMeetingRealtime]
  );

  const handleUpdatePointSummary = useCallback(
    (pointId, summary) => {
      if (!activeMeeting || !pointId || !userIsSecretary) return false;
      const sent = sendMeetingRealtime({ type: 'point_summary_update', point_id: Number(pointId), summary });
      if (!sent) setMeetingError('Enregistrement impossible (connexion temps réel).');
      return sent;
    },
    [activeMeeting, userIsSecretary, sendMeetingRealtime]
  );

  const liveActions = useMemo(
    () => ({
      saveProjectNews: handleUpdateProjectNews,
      savePointSummary: handleUpdatePointSummary,
    }),
    [handleUpdateProjectNews, handleUpdatePointSummary]
  );

  // Focus tech : le secrétaire lance et termine, le présentateur fait défiler et ses gestes sont diffusés.
  const focusActions = useMemo(
    () => ({
      start: (focusId) => sendMeetingRealtime({ type: 'focus_start', focus_id: focusId }),
      end: (presented = true) => sendMeetingRealtime({ type: 'focus_end', presented }),
      slide: (slide, slideCount) => sendMeetingRealtime({ type: 'focus_slide', slide, slide_count: slideCount }),
      sendEvents: (slide, events) => sendMeetingRealtime({ type: 'focus_events', slide, events }),
      sendState: (slide, state) => sendMeetingRealtime({ type: 'focus_state', slide, state }),
      snapshot: () => focusLogRef.current,
      subscribe: (listener) => {
        focusListenersRef.current.add(listener);
        return () => focusListenersRef.current.delete(listener);
      },
    }),
    [sendMeetingRealtime]
  );

  const handleLiveMeetingTensionEdit = (tensionId, updates) => {
    if (!activeMeeting || !tensionId || !userIsSecretary) return;

    // Mise à jour locale immédiate ; les échos du serveur ne l'écrasent pas tant que la saisie est en cours.
    localTensionEdit.current = { id: tensionId, until: Date.now() + LOCAL_TENSION_EDIT_HOLD_MS };
    setMeetingTensions((prev) =>
      prev.map((t) => (String(t.id) === String(tensionId)
        ? { ...t, ...updates, proposed_data: { ...(t.proposed_data || {}), ...(updates.proposed_data || {}) } }
        : t))
    );

    const payload = {
      type: 'tension_update',
      tension_id: tensionId,
      title: updates.title,
      summary: updates.summary,
      action: updates.action,
      proposed_data: updates.proposed_data,
      operations: updates.operations || [],
      expressed_by_role_id: updates.expressed_by_role_id,
    };
    // Saisie encore en attente sur une autre tension : elle part maintenant, sinon elle serait perdue.
    const previous = pendingTensionUpdate.current;
    if (previous && String(previous.tension_id) !== String(tensionId)) sendMeetingRealtime(previous);
    pendingTensionUpdate.current = payload;

    if (tensionEditDebounce.current) clearTimeout(tensionEditDebounce.current);
    tensionEditDebounce.current = setTimeout(() => {
      tensionEditDebounce.current = null;
      if (sendMeetingRealtime(payload)) {
        pendingTensionUpdate.current = null;
        localTensionEdit.current = { id: tensionId, until: Date.now() + LOCAL_TENSION_EDIT_HOLD_MS };
      }
      // Hors ligne : la saisie reste en attente (le relevé ne l'écrase pas) et repart à la reconnexion.
    }, 250);
  };

  const clearMeetingError = useCallback(() => setMeetingError(null), []);

  // Le secrétaire montre une tension à tous : affichée tout de suite chez lui, puis chez les autres par le serveur.
  const handleSelectMeetingTension = useCallback((tensionId) => {
    if (!userIsSecretary) return;
    setSelectedMeetingTensionId(tensionId);
    setSelectedMeetingTensionIdShared(tensionId);
    sendMeetingRealtime({ type: 'select_tension', tension_id: tensionId });
  }, [userIsSecretary, sendMeetingRealtime]);

  const handleMeetingDecision = async (tensionId, decision) => {
    if (!activeMeeting) return;
    if (!userIsSecretary) {
      setMeetingError('Seul le secrétaire peut valider');
      return;
    }
    // La dernière saisie part avant la décision, et le minuteur ne la renverra pas ensuite.
    if (tensionEditDebounce.current) {
      clearTimeout(tensionEditDebounce.current);
      tensionEditDebounce.current = null;
    }
    if (pendingTensionUpdate.current) {
      sendMeetingRealtime(pendingTensionUpdate.current);
      pendingTensionUpdate.current = null;
    }
    localTensionEdit.current = { id: null, until: 0 };
    if (sendMeetingRealtime({ type: 'decision', tension_id: tensionId, decision })) return;
    try {
      const response = await fetch(`${apiUrl}/circle-meetings/${activeMeeting.id}/tensions/${tensionId}/decision?decision=${decision}`, {
        method: 'POST',
        headers: getAuthHeadersRef.current(),
      });
      if (!response.ok) throw new Error(await readErrorDetail(response, 'Décision impossible'));
      await fetchMeetingTensions(activeMeeting.id);
    } catch (err) {
      setMeetingError(err.message);
    }
  };

  return {
    activeMeeting,
    meetingTensions,
    meetingParticipants,
    meetingNotes,
    meetingPresenceCount,
    meetingConnectedUsers,
    meetingRealtimeConnected,
    meetingFormVisible,
    meetingPhase,
    meetingPoints,
    meetingPreparedPoints,
    meetingPreparedPointsLoading,
    meetingPreparedPointsError,
    selectedMeetingPointId,
    selectedMeetingPointIdShared,
    selectedMeetingProjectId,
    selectedMeetingProjectIdShared,
    meetingProjectReviews,
    meetingPointComposer,
    meetingLoading,
    meetingError,
    meetingHistory,
    meetingHistoryLoading,
    meetingHistoryError,
    showMeetingHistory,
    meetingDraft,
    selectedMeetingTensionId,
    selectedMeetingTensionIdShared,
    openMeetings,
    openMeetingsByCircle,
    openMeetingCircleIds,
    setSelectedMeetingTensionId,
    setSelectedMeetingPointId,
    setActiveMeeting,
    fetchMeetingTensions,
    fetchMeetingPreparedPoints,
    fetchMeetingProjectReviews,
    fetchOpenMeetingsForUser,
    sendMeetingRealtime,
    handleMeetingDraftChange,
    handleTransferSecretary,
    handleMeetingNotesChange,
    handleOpenMeeting,
    handleCloseMeeting,
    handleOpenMeetingHistory,
    handleCloseMeetingHistory,
    handleImportMeetingTension,
    handleCreateMeetingTension,
    handleJoinMeeting,
    handleToggleMeetingForm,
    handleAddMeetingPoint,
    handleImportMeetingPoint,
    handleSelectMeetingPoint,
    handleMeetingPointDecision,
    handleMeetingPointToTension,
    handleMeetingPointNotesChange,
    handleMeetingPointComposerUpdate,
    handleSelectMeetingProject,
    handleSelectMeetingPhase,
    handleUpdateMeetingProjectReview,
    handleCreateTacticalTask,
    handleCreateTacticalObjective,
    handleInviteMeetingParticipant,
    handleLiveMeetingTensionEdit,
    handleSelectMeetingTension,
    handleMeetingDecision,
    clearMeetingError,
    meetingLive,
    liveActions,
    meetingFocus,
    meetingFocusList,
    focusActions,
  };
};

export default useMeetingState;
